import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import type { Insurer, InsurerProduct } from '../../../../../types';

import {
  useInsurerGetInsurersQuery,
  useOwnerGetOwnerQuery,
  useOwnerUpdateInsurerAccessesMutation,
  useSnakeBar,
} from '../../../../../hooks';

import { TranslationKeys } from '../../../../../translations';

import {
  ActionButton,
  IsSuperAdmin,
  ListCollapsibleRow,
  ListRow,
  ListRowNoData,
  ListRowSkeleton,
} from '../../../../../components';

import { ContainerBox, DivButtonsContainerStyled } from './InsurerAccessesTab.styles';
import { ProductTypeTag } from './components/ProductTypeTag';

type InsurerAccessesState = Record<string, Array<string>>;

export const InsurerAccessesTab = () => {
  const { ownerId } = useParams();
  const isSuperAdmin = IsSuperAdmin();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const { mutate: updateInsurerAccesses, isLoading: isSaving } = useOwnerUpdateInsurerAccessesMutation();

  const hasOwnerId = !!ownerId;

  const {
    data: owner,
    isLoading: isOwnerLoading,
    refetch: refetchOwner,
  } = useOwnerGetOwnerQuery({
    variables: { ownerId: ownerId || '' },
    options: {
      enabled: hasOwnerId,
    },
  });

  const { data: insurersData, isLoading: isInsurersLoading } = useInsurerGetInsurersQuery({
    variables: { page: 1, pageSize: 500 },
    options: {},
  });

  const insurers = insurersData?.data ?? [];
  const isLoading = isOwnerLoading || isInsurersLoading;

  const [selectedAccesses, setSelectedAccesses] = React.useState<InsurerAccessesState>({});
  const [defaultAccesses, setDefaultAccesses] = React.useState<InsurerAccessesState>({});

  // Initialize state from owner's insurerAccesses
  React.useEffect(() => {
    if (owner?.insurerAccesses) {
      const accesses: InsurerAccessesState = { ...owner.insurerAccesses };
      setSelectedAccesses(accesses);
      setDefaultAccesses(accesses);
    } else if (owner && !owner.insurerAccesses) {
      setSelectedAccesses({});
      setDefaultAccesses({});
    }
  }, [owner]);

  const hasChanges = React.useMemo(() => {
    return JSON.stringify(defaultAccesses) !== JSON.stringify(selectedAccesses);
  }, [defaultAccesses, selectedAccesses]);

  const onToggleInsurer = React.useCallback(
    (insurer: Insurer) => () => {
      setSelectedAccesses(prev => {
        const newState = { ...prev };
        if (insurer.insurerId in newState) {
          delete newState[insurer.insurerId];
        } else {
          // Select insurer with all its products by default
          newState[insurer.insurerId] = (insurer.products || []).map(p => p.productId);
        }
        return newState;
      });
    },
    [],
  );

  const onToggleProduct = React.useCallback(
    (insurerId: string, product: InsurerProduct) => () => {
      setSelectedAccesses(prev => {
        const newState = { ...prev };

        if (!(insurerId in newState)) {
          // If insurer is not selected yet, select it with just this product
          newState[insurerId] = [product.productId];
          return newState;
        }

        const products = [...newState[insurerId]];
        const productIndex = products.indexOf(product.productId);

        if (productIndex !== -1) {
          products.splice(productIndex, 1);
          // If no products left, remove the insurer entirely
          if (products.length === 0) {
            delete newState[insurerId];
          } else {
            newState[insurerId] = products;
          }
        } else {
          products.push(product.productId);
          newState[insurerId] = products;
        }

        return newState;
      });
    },
    [],
  );

  const onClickCancel = React.useCallback(() => {
    setSelectedAccesses(defaultAccesses);
  }, [defaultAccesses]);

  const onClickSave = React.useCallback(() => {
    if (ownerId) {
      updateInsurerAccesses(
        { ownerId, insurerAccesses: selectedAccesses },
        {
          onSuccess: () => {
            showSuccessSnakeBar({ method: 'updateInsurerAccesses' });
            setDefaultAccesses(selectedAccesses);
            refetchOwner();
          },
          onError: (error: any) => {
            showErrorSnakeBar({ method: 'updateInsurerAccesses', message: error?.message });
          },
        },
      );
    }
  }, [ownerId, updateInsurerAccesses, selectedAccesses, showSuccessSnakeBar, showErrorSnakeBar, refetchOwner]);

  if (!isSuperAdmin) {
    return null;
  }

  const hasData = insurers.length > 0;
  const showData = !isLoading && hasData;
  const showNoData = !isLoading && !hasData;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant={'h6'}>
          <FormattedMessage id={TranslationKeys.owners_tab_insurerAccesses} />
        </Typography>
      </Box>

      <Box sx={theme => ({ marginTop: theme.spacing(3) })}>
        <ContainerBox>
          {isLoading && <ListRowSkeleton />}

          {showData &&
            insurers.map(insurer => {
              const isInsurerSelected = insurer.insurerId in selectedAccesses;
              const selectedProducts = selectedAccesses[insurer.insurerId] ?? [];
              const hasProducts = insurer.products && insurer.products.length > 0;

              if (!hasProducts) {
                return (
                  <ListRow
                    key={insurer.insurerId}
                    selected={isInsurerSelected}
                    onSelect={onToggleInsurer(insurer)}
                    disabled={isSaving}
                  >
                    <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
                      <Typography>{insurer.insurerName}</Typography>
                    </Box>
                  </ListRow>
                );
              }

              return (
                <ListCollapsibleRow
                  key={insurer.insurerId}
                  selected={isInsurerSelected}
                  onSelect={onToggleInsurer(insurer)}
                  disabled={isSaving}
                  rowHeader={
                    <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
                      <Typography>{insurer.insurerName}</Typography>
                      <ProductTypeTag type={insurer.type} />
                      <Typography variant="body2" color="text.secondary">
                        {`(${selectedProducts.length} / ${insurer.products.length})`}
                      </Typography>
                    </Box>
                  }
                >
                  {insurer.products.map(product => {
                    const isProductSelected = selectedProducts.includes(product.productId);

                    return (
                      <ListRow
                        key={product.productId}
                        innerRow
                        selected={isProductSelected}
                        onSelect={onToggleProduct(insurer.insurerId, product)}
                        disabled={isSaving || !isInsurerSelected}
                      >
                        <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
                          <Typography variant="body1">{product.productName}</Typography>
                        </Box>
                      </ListRow>
                    );
                  })}
                </ListCollapsibleRow>
              );
            })}

          {showNoData && <ListRowNoData />}
        </ContainerBox>
      </Box>

      {hasChanges && (
        <DivButtonsContainerStyled>
          <ActionButton
            variant={'outlined'}
            messageId={TranslationKeys.button_cancel}
            onClick={onClickCancel}
            disabled={isSaving}
          />
          <ActionButton messageId={TranslationKeys.button_save} onClick={onClickSave} disabled={isSaving} />
        </DivButtonsContainerStyled>
      )}
    </Box>
  );
};
